/**
 * MONSTER TOWN — INSTANCE ART. OWNED BY THE BUILD CREW (src/build/**).
 *
 * Turning a placement into a three.js group, and keeping that group honest when
 * a neighbour changes. This is where LAW #5 (auto-connect, kids cannot build
 * something ugly) is actually enforced at runtime:
 *
 *   place/remove -> grid.refreshSet(changed cells) -> rebuild each returned
 *   placement's art with a FRESH connect mask.
 *
 * `buildArt` is required to be deterministic for the same ArtContext, so a full
 * rebuild is always safe and always produces seamless corners.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import type { Cell, Rot } from '../core/grid'
import { requirePlaceable, type ArtContext } from '../core/placeable'
import type { SceneNode } from '../core/scene'
import type { InstanceId, PlaceableId } from '../core/types'
import { tagInstance } from '../input/pick'
import { stateOf } from '../app/interactions'
import { tintKey, tints } from './state'

/** Stable 0..1 per instance so a row of trees is not identical. */
function varietyOf(id: InstanceId | null): number {
  if (id === null) return 0.5
  let h = 0
  for (let i = 0; i < id.length; i++) h = (Math.imul(h, 31) + id.charCodeAt(i)) >>> 0
  return (h % 1000) / 1000
}

export function artContext(
  node: SceneNode,
  defId: PlaceableId,
  anchor: Cell,
  rot: Rot,
  id: InstanceId | null,
  isGhost: boolean,
): ArtContext {
  const def = requirePlaceable(defId)
  const groupName = def.connect?.group ?? null
  const tint = id === null ? null : (tints.get(tintKey(node.def.id, id)) ?? null)
  return {
    def,
    instance:
      id === null
        ? null
        : { id, defId, sceneId: node.def.id, anchor, rot, state: stateOf(node.def.id, id) ?? {} },
    grid: node.grid,
    anchor,
    rot,
    connect: groupName === null ? 0 : node.grid.connectMask(anchor, groupName),
    connect8: groupName === null ? 0 : node.grid.connectMask8(anchor, groupName),
    tint,
    variety: varietyOf(id),
    ghost: isGhost,
  }
}

/** Dispose nothing — kit geometry and materials are shared and cached. */
function detach(node: SceneNode, id: InstanceId): void {
  const old = node.art.get(id)
  if (old === undefined) return
  node.placeables.remove(old)
  node.art.delete(id)
}

/** Build (or rebuild) one instance's art and drop it into the placeables layer. */
export function placeArt(
  node: SceneNode,
  id: InstanceId,
  defId: PlaceableId,
  anchor: Cell,
  rot: Rot,
): THREE.Object3D {
  const def = requirePlaceable(defId)
  detach(node, id)
  const obj = def.buildArt(artContext(node, defId, anchor, rot, id, false))
  const c = node.grid.footprintCenter(anchor, def.footprint, rot)
  obj.position.set(c.x, c.y, c.z)
  obj.rotation.y = (-rot * Math.PI) / 2
  tagInstance(obj, id)
  node.placeables.add(obj)
  node.art.set(id, obj)
  return obj
}

export function removeArt(node: SceneNode, id: InstanceId): void {
  detach(node, id)
}

/**
 * LAW #5. Rebuild everything whose auto-connect art may have gone stale after
 * the cells in `changed` were occupied or released. Call this after EVERY
 * occupy/release — that is the whole contract that makes corners join.
 */
export function refreshConnected(node: SceneNode, changed: readonly Cell[]): void {
  const stale = node.grid.refreshSet(changed)
  for (const p of stale) placeArt(node, p.instanceId, p.defId, p.anchor, p.rot)
  if (stale.length > 0) bus.emit('build:artStale', { sceneId: node.def.id, placements: stale })
}
